import { formatDayFr } from '@cockpit/shared';
import type { ActionUpdateOp, QueuedOp, R1PrepOp } from './ops';
import { dataTimeLabel } from './cache';

/**
 * Lot AW : feuille « Inès a modifié cette carte pendant que tu étais hors connexion » (maquette, repère 36) :
 * comparaison champ par champ, « Garder ta version » / « Garder la sienne ». Mise en mots, testée sans navigateur.
 */

export type ConflictOp = (ActionUpdateOp | R1PrepOp) & { state: 'conflict' };

export function isConflictOp(op: QueuedOp): op is ConflictOp {
  return op.state === 'conflict' && (op.kind === 'action.update' || op.kind === 'r1.prep') && !!op.conflict;
}

const ACTION_LABELS: Record<string, string> = {
  title: 'Titre',
  description: 'Description',
  dueDate: 'Pour quand',
  assigneeId: 'Pour qui',
  clientId: 'Client',
  projectId: 'Projet',
};

const R1_LABELS: Record<string, string> = {
  company: 'Entreprise',
  website: 'Site',
  channel: 'Canal',
  referrer: 'Recommandé par',
  contactName: 'Contact',
  contactRole: 'Rôle du contact',
  need: 'Besoin',
  notes: 'Notes',
};

export interface ConflictRow {
  label: string;
  mine: string;
  theirs: string;
}

export interface ConflictContext {
  today: string;
  members: readonly { id: string; name: string }[];
  projects?: readonly { id: string; name: string }[];
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

function short(text: string, max = 90): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/** Valeur lisible d'un champ. */
function show(
  field: string,
  value: unknown,
  op: ConflictOp,
  ctx: ConflictContext,
  side: 'mine' | 'theirs',
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (op.kind === 'action.update') {
    switch (field) {
      case 'dueDate':
        return typeof value === 'string' ? formatDayFr(value, ctx.today) : String(value);
      case 'assigneeId':
        return ctx.members.find((m) => m.id === value)?.name ?? 'un autre membre';
      case 'clientId':
        if (side === 'mine') return op.client?.name || 'un autre client';
        return op.conflict?.current.client?.name || 'un autre client';
      case 'projectId':
        return ctx.projects?.find((p) => p.id === value)?.name ?? 'un autre projet';
      default:
        return short(String(value));
    }
  }
  return short(String(value));
}

/** Lignes de la comparaison (un champ par ligne). */
export function conflictRows(op: ConflictOp, ctx: ConflictContext): ConflictRow[] {
  const labels = op.kind === 'action.update' ? ACTION_LABELS : R1_LABELS;
  return (op.conflict?.fields ?? []).map((f) => ({
    label: labels[f.field] ?? f.field,
    mine: show(f.field, f.mine, op, ctx, 'mine'),
    theirs: show(f.field, f.theirs, op, ctx, 'theirs'),
  }));
}

/** Prénom de la personne qui a changé la carte (« Quelqu'un » si inconnue). */
export function conflictWho(op: ConflictOp): string | null {
  const name = op.conflict?.changedBy?.name;
  return name ? firstName(name) : null;
}

export function conflictTitle(op: ConflictOp): string {
  const who = conflictWho(op) ?? 'Quelqu’un';
  return op.kind === 'r1.prep'
    ? `${who} a modifié les infos de ce R1 pendant que tu étais hors connexion`
    : `${who} a modifié cette carte pendant que tu étais hors connexion`;
}

/** « Celle d'Inès · 14 h 58 » / « Ta version · 14 h 50 ». */
export function conflictHeads(op: ConflictOp, now: Date = new Date()): { mine: string; theirs: string } {
  const who = conflictWho(op);
  const theirsAt =
    op.kind === 'action.update' ? op.conflict?.current.updatedAt : op.conflict?.current.prep?.filledAt;
  const theirsLabel = who
    ? `Celle ${/^[aeiouyéèêàâîïôûh]/i.test(who) ? 'd’' : 'de '}${who}`
    : 'L’autre version';
  return {
    mine: `Ta version · ${dataTimeLabel(op.createdAt, now)}`,
    theirs: theirsAt ? `${theirsLabel} · ${dataTimeLabel(theirsAt, now)}` : theirsLabel,
  };
}
